import { defineStore } from "pinia";
import {
  resetAllPiniaStores,
  useLoaderStore,
  useSettingsStore,
  useUsersStore,
  useWorkspaceStore,
} from "./index";
import { useNotificationsStore } from "./notifications.store";
import { useAlertsStore } from "./alerts.store";
import { showInAppAndDesktopNotification } from "@/lib/showNotification";
import { userRoleNames } from "@/lib/audits.js";
import { workspaceHomePath } from "@/composables/useWorkspaceRedirect";
import { toast } from "vue-sonner";
import { authApi as api } from "@/api/endpoints/auth";
import VueCookies from "vue-cookies";
import router from "@/router";
import { bindEchoListeners, disconnectEcho } from "@/lib/realtime";

/** API may return an absolute frontend URL; Vue Router needs path + query + hash. */
function resolveRedirectTarget(target) {
  if (typeof target !== "string" || !target) return null;
  if (target === "training" || target === "policy") return target;
  if (target.startsWith("/")) return target;
  try {
    const url = new URL(target);
    return `${url.pathname}${url.search}${url.hash}` || null;
  } catch {
    return null;
  }
}

/** New-user onboarding routes — never a post-login home for super-admins. */
function isOnboardingReturnPath(target) {
  if (typeof target !== "string" || !target.startsWith("/")) return false;
  const pathname = target.split("?")[0].split("#")[0];
  return (
    pathname === "/welcome" ||
    pathname === "/setup-workspace" ||
    pathname.startsWith("/onboarding/")
  );
}

function userIsSuperAdmin(user) {
  return userRoleNames(user).some((name) =>
    String(name).trim().toLowerCase() === "super-admin",
  );
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: {},
    isLoggedIn: false,
    resettingPassword: false,
    redirectAfterLogin: null,
    loadingProfile: false,
    profileLoaded: false,
    defaultWorkspaceId: null,
    signupEmail: "",
    signupForm: null,
    nextStep: null,
    inviteCode: null,
  }),
  getters: {
    // Profile/login can return `role` as a string or `{ name }`; also check `roles[]`.
    isSuperAdmin: (state) => userIsSuperAdmin(state.user),
    // Require a real user — token/isLoggedIn alone must not unlock auth routes
    // (stale cookie or cleared state would otherwise reach /welcome).
    isAuthenticated: (state) =>
      Boolean(state?.user?.id && (state?.isLoggedIn || VueCookies.isKey("token"))),
    role: (state) => state.user?.role ?? null,
    /** Auditee / Analyst (assignment-scoped audits — not workspace membership). */
    isAuditeeAssignee: (state) => userRoleNames(state.user).some((name) => /auditee|analyst/i.test(name)),
    /** Auditor or Auditee/Analyst — any assignment-scoped audit role, not workspace membership. */
    isAuditAssignee: (state) => userRoleNames(state.user).some((name) => /auditor|auditee|analyst/i.test(name)),
    canEditShareable: (state) => {
      const permissin_level =
        useWorkspaceStore().selectedWorkspace?.permission_level;
      if (state.isSuperAdmin) {
        return true;
      } else {
        return permissin_level === null ? true : permissin_level === "edit";
      }
    },
    allPermissions: (state) => {
      const raw = state.user?.permissions ?? [];
      return raw.flatMap((group) => group.permissions.map((p) => p.name));
    },
  },
  actions: {
    async Login(form) {
      // Logout/$reset wipes store state — keep the URL the auth guard saved
      // (e.g. /onboarding/:token) so HandleRedirections can send the user back.
      const returnTo = this.redirectAfterLogin;
      this.Logout();
      if (returnTo) this.redirectAfterLogin = returnTo;
      const body = await api.login(form);
      const { token, user, redirect_url, next_step, invite_code } = body.data;
      await this.SetupToken({ token, user, redirect_url, next_step, invite_code });
    },

    async SetupToken(payload, redirect = true) {
      VueCookies.set("token", payload.token);
      this.isLoggedIn = true;
      this.user = payload.user;
      this.defaultWorkspaceId = payload.user.default_workspace?.id;

      const pendingReturn = resolveRedirectTarget(this.redirectAfterLogin);
      const apiReturn = resolveRedirectTarget(payload.redirect_url);
      this.redirectAfterLogin = pendingReturn || apiReturn || null;
      this.nextStep = payload.next_step;
      this.inviteCode = payload.invite_code;
      // Super-admins never continue workspace onboarding. Drop next_step and
      // any stale /welcome return saved when a previous session expired there.
      if (userIsSuperAdmin(payload.user)) {
        this.nextStep = null;
        this.inviteCode = null;
        if (isOnboardingReturnPath(this.redirectAfterLogin)) {
          this.redirectAfterLogin = null;
        }
      }
      // Skip the "Setting Up Workspace..." loader when the user still needs
      // to finish welcome / onboarding (no workspace exists yet).
      await this.GetProfile(undefined, { silent: Boolean(this.nextStep) });
      if (redirect || this.nextStep) await this.HandleRedirections();
    },

    async HandleRedirections() {

      if (this.isSuperAdmin) {
        this.nextStep = null;
        this.inviteCode = null;
        const rawReturn = this.redirectAfterLogin;
        this.redirectAfterLogin = null;
        const target = resolveRedirectTarget(rawReturn);
        if (target?.startsWith("/") && !isOnboardingReturnPath(target)) {
          router.replace(target);
          return;
        }
        router.replace("/admin-dashboard");
        return;
      }

      if (this.nextStep) {
        router.replace({
          path: "/welcome",
          query: {
            token: this.inviteCode
          }
        });
        this.nextStep = null;
        this.inviteCode = null;
        return;
      }

      const rawReturn = this.redirectAfterLogin;
      this.redirectAfterLogin = null;
      const target = resolveRedirectTarget(rawReturn);

      if (target === "training") {
        router.replace(
          "/workspaces/" + useWorkspaceStore().getWorkspaceID + "/training",
        );
        return;
      }
      if (target === "policy") {
        router.replace(
          "/workspaces/" +
            useWorkspaceStore().getWorkspaceID +
            "/policy-tracker",
        );
        return;
      }
      if (target?.startsWith("/")) {
        // Path from auth guard, or absolute API redirect_url normalized to path
        // (e.g. https://…/onboarding/:token → /onboarding/:token).
        router.replace(target);
        return;
      }

      // Prefer a real default workspace — do not treat a bare workspace_id
      // alone as enough to enter the dashboard before onboarding finishes.
      const workspaceId =
        this.defaultWorkspaceId ??
        this.user?.default_workspace?.id ??
        useWorkspaceStore().selectedWorkspace?.id;

      if (!workspaceId || workspaceId === "null") {
        router.replace("/welcome");
        return;
      }

      router.replace(workspaceHomePath(workspaceId));
    },

    async GetRedirectionUrl() {
      const body = await api.googleRedirect();
      window.location.href = body.data.redirect_url;
      return body.data;
    },

    async GetProfile(params, { silent = false } = {}) {
      this.loadingProfile = true;
      if (!silent) {
        useLoaderStore().show("Setting Up Workspace...");
      }
      try {
        const body = await api.profile(params);
        const { user } = body.data;

        this.user = user;
        this.defaultWorkspaceId =
          user.default_workspace?.id ?? user.workspace_id ?? this.defaultWorkspaceId;
        // Super-admins manage many workspaces; do not pin selectedWorkspace
        // to a single default (role may arrive as string or { name }).
        if (!userIsSuperAdmin(user) && user.default_workspace?.id) {
          this.user = { ...user, workspace_id: user.default_workspace?.id };
          this.defaultWorkspaceId = user.default_workspace.id;
          useWorkspaceStore().SetupSharedWorkspace(user.workspaces);
          const routeWorkspaceId = router.currentRoute.value.params.workspace_id;
          if (routeWorkspaceId && routeWorkspaceId !== "null" && routeWorkspaceId !== "undefined") {
            await useWorkspaceStore().SelectWorkspaceByID(routeWorkspaceId);
          } else {
            await useWorkspaceStore().SelectWorkspace(user.default_workspace);
          }
        }
      } finally {
        this.loadingProfile = false;
        this.profileLoaded = true;
        if (!silent) {
          useLoaderStore().hide();
        }
        this.initializeReverb();
        // Only hydrate notifications once a real default workspace exists —
        // onboarding users on /welcome have no workspace yet.
        if (
          this.user?.default_workspace?.id &&
          useWorkspaceStore().getWorkspaceID
        ) {
          useNotificationsStore()
            .fetchNotifications()
            .catch(() => {});
        }
      }
    },

    ApplyDefaultWorkspace(workspace) {
      if (!workspace?.id) return;

      this.defaultWorkspaceId = workspace.id;
      const workspaces = [
        ...(this.user.workspaces ?? []).filter((ws) => ws.id !== workspace.id),
        workspace,
      ];

      this.user = {
        ...this.user,
        workspace_id: workspace.id,
        default_workspace: workspace,
        workspaces,
      };

      const workspaceStore = useWorkspaceStore();
      workspaceStore.SetupSharedWorkspace(workspaces);
      workspaceStore.SelectWorkspace(workspace);
    },

    syncAfterWorkspaceSetup() {
      this.echoMessages();
      useNotificationsStore().fetchNotifications().catch(() => {});
    },

    /** Safe to call on every profile load — see `src/lib/realtime.js`. */
    initializeReverb() {
      this.echoMessages();
    },

    echoMessages() {
      const workspaceStore = useWorkspaceStore();
      const workspaceId = workspaceStore.getWorkspaceID;
      const userId = this.user?.id;
      if (!workspaceId || !userId) return;

      const notificationsStore = useNotificationsStore();
      const alertsStore = useAlertsStore();
      const usersStore = useUsersStore();
      const userChannel = `workspace.${workspaceId}.users.${userId}`;
      const workspaceChannel = `workspace.${workspaceId}`;

      const resolveLink = (link) => {
        if (!link) return null;
        if (link.startsWith("/")) return link;
        return `/workspaces/${workspaceId}/${link.replace(/^\//, "")}`;
      };

      const presentNotification = (payload) => {
        const notification = notificationsStore.addNotification({
          id: payload.id,
          title: payload.title,
          message: payload.message,
          type: payload.type,
          link: payload.link,
          created_at: payload.created_at,
        });
        if (!notification?.title) return;

        showInAppAndDesktopNotification(notification, {
          onDesktopClick: () => {
            const to = resolveLink(notification.link);
            if (to) router.push(to);
          },
        });
      };

      const ingestAlert = (notify) => (payload) => {
        alertsStore.ingestAlert(payload?.alert ?? payload, { notify });
      };

      // Personal channel is ours alone, so leave it on workspace switch.
      // The workspace channel is shared with page composables
      // (useAuditItemStatusUpdates), so only our listeners are removed.
      bindEchoListeners(`${workspaceId}:${userId}`, [
        { channel: userChannel, leave: true, event: ".workspace.user.created", handler: presentNotification },
        // Documented general notification event
        { channel: userChannel, leave: true, event: ".workspace.notification.created", handler: presentNotification },
        // Reminder & alert system — real-time high-priority items
        { channel: userChannel, leave: true, event: ".workspace.alert.created", handler: ingestAlert(true) },
        { channel: userChannel, leave: true, event: ".workspace.alert.updated", handler: ingestAlert(false) },
        {
          channel: userChannel,
          leave: true,
          event: ".workspace.alert.resolved",
          handler: (payload) => {
            const id = payload?.id ?? payload?.alert?.id;
            if (id) alertsStore.dismiss(id);
          },
        },
        {
          channel: userChannel,
          leave: true,
          event: ".questionnaire.ai_fill.ready",
          handler: (payload) => {
            notificationsStore.addNotification({
              id: payload.id,
              title: payload.title,
              message: payload.message,
              type: payload.type,
              link: payload.link,
              created_at: payload.created_at,
            });
          },
        },
        {
          channel: workspaceChannel,
          event: ".workspace.user.created",
          handler: () => {
            usersStore.GetAllUsers({ take_all: true }, true).catch(() => {});
          },
        },
        { channel: workspaceChannel, event: ".workspace.alert.created", handler: ingestAlert(true) },
      ]);
    },
    async SignUpWithGoogle(params) {
      try {
        const body = await api.googleCallback(params);
        const { token, user, workspace_id } = body.data;
        this.SetupToken({ token, user: { ...user, workspace_id } });
        return body.data;
      } catch (error) {
        if (error.status === 409) {
          router.push(`/complete-your-registration`);
        }
        throw error;
      }
    },

    async SetupAccount(payload) {
      const body = await api.register(payload);
      const { token, user, redirect_url, next_step, invite_code } =
        body.data;
      // Match Login: forward next_step so post-register users land on /welcome
      // instead of a workspace dashboard (workspace is created later in onboarding).
      // Do not inject workspace_id — that falsely enables dashboard/notification APIs.
      await this.SetupToken({
        token,
        user,
        redirect_url,
        next_step: next_step ? "welcome":null,
        invite_code,
      });
      return body.data;
    },

    async Signup(payload) {
      const body = await api.signup(payload);
      this.signupEmail = payload.email;
      const { password: _password, password_confirmation: _passwordConfirmation, ...safeForm } = payload;
      this.signupForm = { ...safeForm };
      return body.data;
    },

    async ResendVerification(payload) {
      const body = await api.resendVerification(payload);
      toast.success(body?.message || "Verification email resent.");
      return body.data;
    },

    async VerifyEmail(payload) {
      const body = await api.verifyEmail(payload);
      if (body.data?.token) {
        const { token, user, redirect_url } = body.data;
        this.SetupToken({ token, user, redirect_url }, false);
      }
      return body.data;
    },

    async UpdateProfile(payload) {
      const body = await api.updateProfile(this.user.id, payload);

      const { first_name, last_name, phone, avatar } = body.data;
      this.user = { ...this.user, first_name, last_name, phone, avatar };
      toast.success(body.message);
      return body.data;
    },

    async ForgotPassword(payload) {
      const body = await api.forgotPassword(payload);
      toast.success(body?.message);
      return body.data;
    },
    async ResetPassword(payload) {
      const body = await api.resetPassword(payload);
      toast.success(body.message);
      return body.data;
    },
    async VerifyResetPasswordToken(payload) {
      const body = await api.verifyResetPasswordToken(payload);
      toast.success(body.message);
      return body.data;
    },
    Logout() {
      this.Reset();
      useSettingsStore().GetGeneralSettings();
    },
    Reset() {
      disconnectEcho();
      VueCookies.remove("token");
      this.user = {};
      this.isLoggedIn = false;
      this.signupEmail = "";
      this.signupForm = null;
      useNotificationsStore().reset();
      useAlertsStore().reset();
      resetAllPiniaStores();
      router.push("/login");
    },
    /**
     * Single entry point for the 401 handling in the fetch clients (main API + Policy AI).
     * Guarded so concurrent 401s (e.g. a long-running AI request and the
     * background alerts poll failing around the same time) only tear down
     * the session once, and always tells the user why they were signed out
     * instead of redirecting silently.
     */
    SessionExpired() {
      if (!this.isLoggedIn) return;
      toast.error("Your session has expired. Please sign in again.");
      this.Reset();
    },
  },
  persist: {
    // Keep return path across login refresh / Logout reset so deep links
    // (and super-admin post-login) still honor the original destination.
    pick: ["isLoggedIn", "user", "signupEmail", "signupForm", "redirectAfterLogin"],
  },
});